import { useMemo } from 'react'
import { useState } from 'react'
import { Title } from 'react-head'
import styled from 'styled-components'
import { Button, Icon, ResponsiveImage } from '@bm-js/atlascloud-ui'
import CalendarItemForm, {
  CalendarItemInput,
  RepeatOptions,
} from '../components/CalendarItemForm'
import Loader from '../components/Loader'
import { breakPoints, colors } from '../utils/config'
import { fetcher } from '../utils/fetcher'
import DeleteConfirmation from '../components/DeleteConfirmation'
import {
  CalendarBM,
  CalendarItem,
  encodeSweUrlComponent,
  LucideIconName,
  parseDateTime,
} from '@bm-js/atlascloud-shared'
import { usePopup, useToast, useToken, useWebsite } from '../utils/hooks'
import dayjs from 'dayjs'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'

const StyledCalendarRoute = styled.div`
  .loader-container {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
  }
  .event {
    padding: 1rem;
    img {
      width: 50px;
      height: 50px;
      object-fit: contain;
      margin: 0;
    }
    display: grid;
    grid-template-columns: 50px 1fr;
    gap: 3rem;
    align-items: center;
    .event-text-content {
      display: grid;
      grid-template-columns: 2fr 3fr 1fr;
      justify-items: flex-start;
      align-items: center;
    }
    .actions {
      justify-self: flex-end;
      gap: 0.5rem;
    }
    position: relative;
    &:after {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 1px;
      background: ${colors.blackAlpla300};
      content: '';
    }
  }
`

const CalendarRoute = () => {
  const toast = useToast()
  const token = useToken()
  const { tr } = useTr()
  const { atlasWebsite } = useWebsite()
  const { triggerRebuild, refetchAtlasWebsite } = useWebsite()
  const { closePopup, setPopup } = usePopup()

  const [loading, setLoading] = useState(false)

  const calendar = useMemo(() => {
    return atlasWebsite?.calendars[0]
  }, [atlasWebsite])

  const events = useMemo(() => {
    const items = calendar?.items || []
    const repeatKeys = Array.from(
      new Set(items.map((it) => it.repeatKey).filter(Boolean))
    )
    const parsedRepeatEvents: CalendarItem[] = []
    repeatKeys.forEach((key) => {
      parsedRepeatEvents.push(items.filter((it) => it.repeatKey === key)[0])
    })
    const filteredEvents = items
      .filter((it) => !it.repeatKey)
      .concat(parsedRepeatEvents)

    const sortedEvents = filteredEvents.sort((a, b) =>
      dayjs(a.start).isBefore(dayjs(b.start)) ? -1 : 1
    )
    return sortedEvents
  }, [calendar])

  const createEvent = async (
    formData: CalendarItemInput,
    cb: () => void,
    errCb: () => void,
    repeatOptions?: RepeatOptions
  ) => {
    setLoading(true)
    const body: { item: CalendarItemInput; repeatOptions?: RepeatOptions } = {
      item: {
        ...formData,
        urlTitle: encodeSweUrlComponent(formData.summary),
      },
    }
    if (repeatOptions) {
      body.repeatOptions = repeatOptions
    }
    const { err } = await fetcher<CalendarBM>({
      method: 'POST',
      path: `customer/calendar/${calendar?._id}`,
      body,
      auth: token,
    })
    if (err) {
      errCb()
      toast.error(tr(TrKey.calendarCouldNotSaveEvent))
      setLoading(false)
      return
    }
    cb()
    toast.success(tr(TrKey.calendarEventSaved))
    closePopup()
    await refetchAtlasWebsite()
    await triggerRebuild()
    setLoading(false)
  }

  const createEventInit = () => {
    setPopup(<CalendarItemForm handleSubmit={createEvent} />)
  }

  const deleteInit = (event: CalendarItem) => {
    setPopup(
      <DeleteConfirmation
        item={event}
        deleteKey='summary'
        handleDelete={deleteEvent}
        handleCancel={closePopup}
        name={tr(TrKey.calendarTheEvent)}
      />
    )
  }

  const deleteEvent = async (event: CalendarItem, cb: () => void) => {
    setLoading(true)
    const { err } = await fetcher({
      path: `customer/calendar/${calendar?._id}/${event._id}`,
      method: 'DELETE',
      auth: token,
    })
    if (err) {
      cb()
      setLoading(false)
      toast.error(tr(TrKey.calendarCouldNotDeleteEvent))
      return
    }
    cb()
    closePopup()
    toast.success(tr(TrKey.calendarEventDeleted))
    await refetchAtlasWebsite()
    await triggerRebuild()
    setLoading(false)
  }

  const updateInit = (event: CalendarItem) => {
    setPopup(<CalendarItemForm data={event} handleSubmit={updateEvent} />)
  }

  const updateEvent = async (
    event: CalendarItemInput,
    cb: () => void,
    errCb: () => void,
    repeatOptions?: RepeatOptions
  ) => {
    setLoading(true)
    const body = {
      ...event,
      urlTitle: encodeSweUrlComponent(event.summary),
      repeatOptions,
    }
    const { err } = await fetcher({
      path: `customer/calendar/${calendar?._id}/${event._id}`,
      method: 'POST',
      body,
      auth: token,
    })
    if (err) {
      setLoading(false)
      errCb()
      toast.error(tr(TrKey.calendarCouldNotUpdateEvent))
      return
    }
    toast.success(tr(TrKey.calendarEventUpdated))
    cb()
    await refetchAtlasWebsite()
    await triggerRebuild()
    closePopup()
    setLoading(false)
  }

  const createDateString = (event: CalendarItem) => {
    const weekdays = [
      tr(TrKey.weekdayMondayPlural),
      tr(TrKey.weekdayTuesdayPlural),
      tr(TrKey.weekdayWednesdayPlural),
      tr(TrKey.weekdayThursdayPlural),
      tr(TrKey.weekdayFridayPlural),
      tr(TrKey.weekdaySaturdayPlural),
      tr(TrKey.weekdaySundayPlural),
    ]

    const parseTime = (d: string) => {
      return new Date(d).toLocaleTimeString('sv', {
        hour: '2-digit',
        minute: '2-digit',
      })
    }

    let ret = `${parseDateTime(event.start)} - ${parseDateTime(event.end)}`
    if (event.repeatOptions) {
      if (event.repeatOptions.unit === 'day') {
        ret = tr(TrKey.calendarRepeatStringDay, {
          startTime: parseTime(event.start),
          endTime: parseTime(event.end),
        })
      }
      if (event.repeatOptions.unit === 'week') {
        ret = tr(TrKey.calendarRepeatStringWeek, {
          weekday: weekdays[new Date(event.start).getDay()],
          startTime: parseTime(event.start),
          endTime: parseTime(event.end),
        })
      }
    }
    return ret
  }

  const tablet = window.innerWidth < breakPoints.tablet

  const title = useMemo(() => {
    return tr(TrKey.calendarTitle) + ' | Atlascloud'
  }, [tr])

  if (!atlasWebsite || loading)
    return (
      <StyledCalendarRoute>
        <div className='loader-container'>
          <Loader color={colors.primary400} />
        </div>
      </StyledCalendarRoute>
    )

  return (
    <StyledCalendarRoute className='content'>
      <Title>{title}</Title>
      <div className='headers jcsb ac flex'>
        <h1 className='m0 primblue l'>{tr(TrKey.calendarTitle)}</h1>
        <Button onClick={createEventInit}>
          {tr(TrKey.calendarCreateEvent)}
        </Button>
      </div>
      <div className='events-container'>
        {events.length ? (
          events.map((event) => (
            <div className='event' key={event._id}>
              <ResponsiveImage file={event.image} />
              <div className='event-text-content'>
                <p className='m0'>{event.summary}</p>
                <p className='m0'>{createDateString(event)}</p>
                <div className='actions flex'>
                  <Button
                    disabled={loading}
                    onClick={() => updateInit(event)}
                    withIcon={tablet}
                    className='text-white'
                  >
                    {tablet ? (
                      <Icon name={LucideIconName.Pen} className='size-5' />
                    ) : (
                      tr(TrKey.edit)
                    )}
                  </Button>
                  <Button
                    disabled={loading}
                    danger
                    onClick={() => deleteInit(event)}
                    withIcon={tablet}
                    className='text-white'
                  >
                    {tablet ? (
                      <Icon name={LucideIconName.Trash2} className='size-5' />
                    ) : (
                      tr(TrKey.delete)
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p>{tr(TrKey.calendarNoEventsFound)}</p>
        )}
      </div>
    </StyledCalendarRoute>
  )
}

export default CalendarRoute
